/**
 * Wattec — one "Configure your PC" panel on the product page
 *
 * The product page used to show its choices in two places: the variant
 * picker's "Configure your PC" panel (processor, graphics card, colour —
 * snippets/wattec-variant-picker.liquid) and, under it, Easify's options
 * (memory, storage, Windows, Extras and the peripherals), each in a box of
 * its own. This brings Easify's option set inside the panel, under a small
 * "Upgrades & extras" divider, and gives each of its options the same
 * heading as the variant groups: an icon badge, the option's name, and what
 * is currently chosen on the right. On a product with no variants the panel
 * is drawn around Easify's options alone, so every build reads the same.
 *
 * Only the option set's place on the page changes. Easify still draws every
 * control, takes every press and prices and carries the build to the cart:
 * its fields name the product form themselves (form="product-form-…"), so
 * they belong to it wherever they sit. The panel is the theme's own markup
 * and the variant picker never redraws it, so nothing takes the set back out.
 *
 * Also, on every product page, the custom builder included: in a tick-box
 * list (several monitors, several fans) Easify starts with its "No Thanks"
 * entry ticked and leaves it ticked when a real part is chosen, so the order
 * read "No Thanks - Skip Monitor, MSI MAG 255F …". Ticking a part now unticks
 * the "No …" entry, and ticking that entry unticks the parts.
 *
 * And product tags "hide-option:<option>" / "hide-value:<choice>" leave an
 * option, or one choice, of a shared option set out (see hideOptions()).
 */
(function () {
  'use strict';

  /* ---- small helpers ------------------------------------------------ */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function flatten(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  /** "<name> [ +£130.99 ]" or "<name> | 2" down to the name itself. */
  function plainLabel(text) {
    return flatten(text)
      .replace(/\s*\|\s*\d+\s*$/, '')
      .replace(/\s*[\[(]\s*\+?\s*£?\s*[\d,]+(?:\.\d+)?\s*[\])]\s*$/, '')
      .trim();
  }

  /** Easify's stand-in entries: "Default", "No Thanks - Skip Monitor",
   *  "Thanks No Extras!", "No Custom Fans". */
  function isNone(label) {
    return /^(default|none\b|no\b|skip\b|thanks,?\s+no\b)/i.test(plainLabel(label));
  }

  /** Presses a control the way a finger does. Easify listens for the
   *  pointer and mouse events before a click, not for the click alone
   *  (see press() in assets/wattec-pc-builder.js). */
  function press(node) {
    if (!node) return;
    var options = {
      bubbles: true, cancelable: true, composed: true, view: window,
      button: 0, buttons: 1, detail: 1, pointerId: 1, pointerType: 'mouse', isPrimary: true
    };
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(function (type) {
      var Kind = type.indexOf('pointer') === 0 && window.PointerEvent ? window.PointerEvent : window.MouseEvent;
      node.dispatchEvent(new Kind(type, options));
    });
  }

  function groupType(group) {
    return String(group.id || '').split(',').pop();
  }

  function groupLabel(group) {
    var label = group.querySelector('.tpo_option-label-container .form__label');
    return label ? flatten(label.textContent) : '';
  }

  /* ---- "No Thanks" never ticked alongside a part ---------------------- */

  function labelFor(input) {
    return input.closest('label') || input;
  }

  function settleNone(group, pressed) {
    var boxes = [].slice.call(group.querySelectorAll('input[type="checkbox"]'));
    var parts = boxes.filter(function (box) { return box.checked && !isNone(box.value); });
    var nones = boxes.filter(function (box) { return box.checked && isNone(box.value); });
    if (!parts.length || !nones.length) return;
    // Pressed once per change: should Easify ever ignore the press, this
    // must not keep pressing.
    var now = Date.now();
    if (group.__wtSettled && now - group.__wtSettled < 1500) return;
    group.__wtSettled = now;
    // The customer's latest press decides which side stays ticked.
    var keepNone = pressed && pressed.checked && isNone(pressed.value);
    (keepNone ? parts : nones).forEach(function (box) { press(labelFor(box)); });
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var card = event.target.closest('.tpo_option-container[id$=",checkbox"] .tpo_radio-button-wrapper');
    if (!card) return;
    var group = card.closest('.tpo_option-container');
    var input = card.querySelector('input[type="checkbox"]');
    // Easify ticks the box on the press itself; read it once that is done.
    setTimeout(function () { settleNone(group, input); }, 80);
  }, true);

  /* ---- the panel -------------------------------------------------------
   * Everything below runs only on product templates that use the theme's
   * Easify styling (.wt-easify), and never on the custom builder, which
   * lays Easify out as its own step-by-step flow.
   * ------------------------------------------------------------------ */

  /* Memory and storage are the Key specs panel's own drawings
     (snippets/wattec-spec-icon.liquid); the rest are drawn to match them. */
  var ICONS = {
    ram: '<svg class="wt-spec-icon" viewBox="0 0 100 60" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="5" width="90" height="38" rx="3"/><rect x="10" y="12" width="13" height="22" rx="2"/><rect x="27" y="12" width="13" height="22" rx="2"/><rect x="44" y="12" width="13" height="22" rx="2"/><rect x="61" y="12" width="13" height="22" rx="2"/><rect x="78" y="12" width="13" height="22" rx="2"/><rect x="5" y="43" width="38" height="12" rx="1"/><rect x="57" y="43" width="38" height="12" rx="1"/></svg>',
    storage: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="9" width="16" height="6" rx="0.5"/><rect x="18" y="10" width="2" height="4" rx="0.3"/><rect x="5" y="10" width="1.5" height="1.5"/><rect x="8" y="10" width="1.5" height="1.5"/><rect x="11" y="10" width="1.5" height="1.5"/><rect x="5" y="12.5" width="1.5" height="1.5"/><rect x="8" y="12.5" width="1.5" height="1.5"/><line x1="14" y1="10" x2="16" y2="10"/><line x1="14" y1="12" x2="16" y2="12"/><line x1="14" y1="14" x2="16" y2="14"/></svg>',
    os: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"><path d="M3 5.6l7-1v6.9H3z"/><path d="M12 4.3l9-1.3v8.5h-9z"/><path d="M3 12.5h7v6.9l-7-1z"/><path d="M12 12.5h9V21l-9-1.3z"/></svg>',
    extras: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="8" height="8" rx="1.5"/><rect x="13" y="3" width="8" height="8" rx="1.5"/><rect x="3" y="13" width="8" height="8" rx="1.5"/><path d="M17 13.5v7M13.5 17h7"/></svg>',
    monitor: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="4" width="19" height="12.5" rx="1.5"/><path d="M12 16.5v4M8.5 20.5h7"/></svg>',
    keyboard: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6.5" width="20" height="11" rx="1.5"/><path d="M5.5 10h1M9 10h1M12.5 10h1M16 10h1M5.5 13.5h1M17.5 13.5h1M9 13.5h6"/></svg>',
    mouse: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="6.5" y="2.5" width="11" height="19" rx="5.5"/><path d="M12 6v3.5"/></svg>',
    headphones: '<svg class="wt-spec-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="2.5" y="14" width="4.5" height="7" rx="1.5"/><rect x="17" y="14" width="4.5" height="7" rx="1.5"/></svg>'
  };

  var KINDS = [
    ['ram', /\bram\b|memory/i],
    ['storage', /storage|ssd|nvme|hard drive/i],
    ['os', /windows|operating system/i],
    ['monitor', /monitor/i],
    ['keyboard', /keyboard/i],
    ['mouse', /\bmouse\b/i],
    ['headphones', /headphone|headset/i],
    ['extras', /extras|setup|add-?ons?|accessor/i]
  ];

  function kindOf(label) {
    for (var i = 0; i < KINDS.length; i++) {
      if (KINDS[i][1].test(label)) return KINDS[i][0];
    }
    return 'other';
  }

  /** What a group currently holds, as the panel's heading shows it. */
  function chosenIn(group) {
    var type = groupType(group);

    if (type === 'dropdown') {
      var field = group.querySelector('input.tpo_option-dropdown');
      var picked = field ? plainLabel(field.value) : '';
      return picked ? { text: isNone(picked) ? 'None' : picked, real: !isNone(picked) } : { text: '', real: false };
    }

    if (type === 'checkbox') {
      var parts = [].slice.call(group.querySelectorAll('input[type="checkbox"]')).filter(function (box) {
        return box.checked && !isNone(box.value);
      });
      if (!parts.length) return { text: 'None', real: false };
      // Counted the way the list's own field counts them: by the unit, so
      // two of one monitor and one of another read "3 monitors".
      var units = 0, count = 1;
      parts.forEach(function (box) {
        var card = box.closest('.container-wrap-selection') || box.parentNode;
        var qty = card && card.querySelector('.tpo_quantity-selector_input');
        count = qty ? parseInt(qty.value, 10) : 1;
        units += count > 0 ? count : 1;
      });
      if (parts.length > 1) {
        var nouns = group.__wtNouns || ['item', 'items'];
        return { text: units + ' ' + nouns[1], real: true };
      }
      return { text: (units > 1 ? units + ' × ' : '') + plainLabel(parts[0].value), real: true };
    }

    // The real checked state first: Easify's tpo_checked class can linger on
    // the button that was chosen before.
    var checked = group.querySelector('input[type="radio"]:checked') || group.querySelector('input.tpo_checked');
    if (!checked) return { text: '', real: false };
    var value = plainLabel(checked.value);
    if (isNone(value)) return { text: 'None', real: false };
    // A yes/no option whose "yes" is its own name (Extras: "Extras").
    if (value.toLowerCase() === groupLabel(group).toLowerCase()) value = 'Added';
    return { text: value, real: true };
  }

  function decorate(group) {
    var head = group.querySelector('.tpo_option-label-container');
    if (!head) return;

    if (!head.querySelector('.wt-cfg__icon')) {
      var kind = kindOf(groupLabel(group));
      group.classList.add('wt-cfg-group', 'wt-cfg-group--' + kind);
      var icon = el('span', 'wt-vp__icon wt-cfg__icon');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = ICONS[kind] || '<span class="wt-vp__dot"></span>';
      head.insertBefore(icon, head.firstChild);
      head.appendChild(el('span', 'wt-vp__chosen wt-cfg__chosen'));
    }

    if (groupType(group) === 'checkbox') {
      ensurePicker(group, kindOf(groupLabel(group)));
      updatePicker(group);
    }

    var now = chosenIn(group);
    var chosen = head.querySelector('.wt-cfg__chosen');
    if (chosen.textContent !== now.text) chosen.textContent = now.text;
    group.classList.toggle('is-picked', now.real);
  }

  /* ---- a tick-box list behind a field of its own ----------------------
   * Easify draws a tick-box list (the monitors) fully open, so switching
   * Extras on dropped fifty cards into the page. Here it sits closed behind
   * a field like the keyboard, mouse and headset pickers: the field says
   * what is chosen, a tap opens the list, a second tap — or "Done" under
   * the list — closes it again. Only the list's visibility changes; every
   * tick and quantity is still Easify's own control.
   * ------------------------------------------------------------------ */

  var NOUNS = { monitor: ['monitor', 'monitors'] };

  var CHEVRON = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M3.3 5.6L8 10.3l4.7-4.7 1.1 1.1L8 12.5 2.2 6.7z" fill="currentColor"/></svg>';

  function setOpen(group, open) {
    group.classList.toggle('is-open', open);
    var picker = group.querySelector('.wt-cfg__picker');
    if (picker) picker.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function ensurePicker(group, kind) {
    var list = group.querySelector('.tpo_radio-buttons-wrapper');
    if (!list || group.querySelector('.wt-cfg__picker')) return;
    if (!list.id) list.id = 'wt-cfg-list-' + Math.random().toString(36).slice(2, 9);

    var picker = el('button', 'wt-cfg__picker');
    picker.type = 'button';
    picker.setAttribute('aria-controls', list.id);
    picker.setAttribute('aria-expanded', 'false');
    picker.appendChild(el('img', 'wt-cfg__picker-thumb'));
    picker.appendChild(el('span', 'wt-cfg__picker-text'));
    var chevron = el('span', 'wt-cfg__picker-chevron');
    chevron.innerHTML = CHEVRON;
    picker.appendChild(chevron);
    picker.querySelector('img').alt = '';
    picker.querySelector('img').hidden = true;
    list.parentNode.insertBefore(picker, list);

    var done = el('button', 'wt-cfg__done', 'Done');
    done.type = 'button';
    list.parentNode.insertBefore(done, list.nextSibling);

    picker.addEventListener('click', function () {
      setOpen(group, !group.classList.contains('is-open'));
    });
    done.addEventListener('click', function () {
      setOpen(group, false);
      // Back to the field, which the closing list may have left above the
      // screen, clear of the sticky header.
      var top = picker.getBoundingClientRect().top;
      if (top < 110) window.scrollBy({ top: top - 120, behavior: 'smooth' });
    });

    group.classList.add('wt-cfg-group--collapsible');
    group.__wtNouns = NOUNS[kind] || ['item', 'items'];
  }

  /** The field's summary of what is ticked in its list. */
  function updatePicker(group) {
    var picker = group.querySelector('.wt-cfg__picker');
    if (!picker) return;
    var nouns = group.__wtNouns || ['item', 'items'];
    var ticked = [].slice.call(group.querySelectorAll('input[type="checkbox"]')).filter(function (box) {
      return box.checked && !isNone(box.value);
    });
    var units = 0;
    ticked.forEach(function (box) {
      var card = box.closest('.container-wrap-selection') || box.parentNode;
      var qty = card && card.querySelector('.tpo_quantity-selector_input');
      var count = qty ? parseInt(qty.value, 10) : 1;
      units += count > 0 ? count : 1;
    });

    var text, image = '';
    if (!ticked.length) {
      text = 'Choose ' + nouns[1];
    } else if (ticked.length === 1) {
      text = (units > 1 ? units + ' × ' : '') + plainLabel(ticked[0].value);
      var card = ticked[0].closest('.tpo_radio-button-wrapper');
      var img = card && card.querySelector('img.tpo-image-select-value');
      image = img ? img.getAttribute('src') : '';
    } else {
      text = units + ' ' + nouns[1] + ' selected';
    }

    var label = picker.querySelector('.wt-cfg__picker-text');
    if (label.textContent !== text) label.textContent = text;
    var thumb = picker.querySelector('.wt-cfg__picker-thumb');
    if (image && thumb.getAttribute('src') !== image) thumb.setAttribute('src', image);
    thumb.hidden = !image;
  }

  /** Marks each quantity stepper that stands at one. Easify's minus takes
   *  the part off altogether there, so it is drawn as a remove cross
   *  (wattec-configure.css) and both buttons say what they do. */
  function markSteppers(group) {
    [].forEach.call(group.querySelectorAll('.tpo_quantity-selector_wrapper'), function (stepper) {
      var box = stepper.querySelector('.tpo_quantity-selector_input');
      var count = box ? parseInt(box.value, 10) : 1;
      var atOne = !(count > 1);
      stepper.classList.toggle('is-min', atOne);
      var minus = stepper.querySelector('.tpo_quantity-selector_minus');
      var plus = stepper.querySelector('.tpo_quantity-selector_plus');
      var less = atOne ? 'Remove' : 'One fewer';
      if (minus && minus.title !== less) {
        minus.title = less;
        minus.setAttribute('aria-label', less);
      }
      if (plus && plus.title !== 'One more') {
        plus.title = 'One more';
        plus.setAttribute('aria-label', 'One more');
      }
    });
  }

  function shell() {
    var panel = el('div', 'wt-vp wt-cfg--solo');
    var head = el('div', 'wt-vp__head');
    head.appendChild(el('span', 'wt-vp__eyebrow', 'Configure your PC'));
    head.appendChild(el('span', 'wt-vp__hint', 'Choose your upgrades'));
    panel.appendChild(head);
    return panel;
  }

  function mount(section, set) {
    var panel = section.querySelector('.wt-vp');
    if (!panel) {
      panel = shell();
      // On no-variant builds Easify puts its options inside the Add to Cart
      // row, a flex line where the panel would shrink to a sliver. The panel
      // goes just above that row instead (still inside the product form).
      var row = set.closest('.product-form__buttons');
      var spot = row || set;
      spot.parentNode.insertBefore(panel, spot);
    }
    if (set.parentNode !== panel) {
      if (panel.querySelector('.wt-vp__group')) {
        var hint = panel.querySelector('.wt-vp__hint');
        if (hint) hint.textContent = 'Choose your spec, upgrades and extras';
        panel.appendChild(el('div', 'wt-cfg__sub', 'Upgrades & extras'));
      }
      panel.appendChild(set);
    }
    panel.classList.add('wt-cfg');
  }

  function start() {
    if (document.body.classList.contains('wpb-page')) return true;
    var section = document.querySelector('.main-product-template.wt-easify');
    if (!section) return true;
    var set = section.querySelector('.tpo_option-set-wrapper');
    if (!set || !set.querySelector('.tpo_option-container')) return false;

    mount(section, set);

    var timer = null;
    function refresh() {
      timer = null;
      [].forEach.call(set.querySelectorAll('.tpo_option-container'), function (group) {
        try { decorate(group); } catch (error) { /* one odd group never stops the rest */ }
        if (groupType(group) === 'checkbox') {
          markSteppers(group);
          settleNone(group, null);
        }
      });
    }
    function soon(ms) {
      clearTimeout(timer);
      timer = setTimeout(refresh, ms);
    }

    refresh();

    // A memory card's words sit beside its picture tile rather than inside
    // it (wattec-configure.css), so a press on them is handed to the tile.
    set.addEventListener('click', function (event) {
      var card = event.target.closest && event.target.closest('.tpo_swatches-label_position');
      if (!card || event.target.closest('label')) return;
      var tile = card.querySelector(':scope > label');
      if (tile) press(tile);
    });

    // A choice lands a moment after the press, once Easify has handled it.
    ['click', 'change', 'input'].forEach(function (type) {
      set.addEventListener(type, function () {
        soon(120);
        setTimeout(function () { soon(0); }, 600);
      }, true);
    });
    // Easify redraws an option when another one reveals or hides it.
    if (window.MutationObserver) {
      new MutationObserver(function () { if (!timer) soon(150); })
        .observe(set, { childList: true, subtree: true });
    }
    return true;
  }

  /* ---- options a product leaves out --------------------------------- */

  /* Easify shows one option set per product, so builds that share a set but
     start from different parts need some choices left out on some of them
     (the DDR4 builds share one: CLICK ships with 16GB and is offered 32GB or
     64GB, ClipMachine ships with 32GB, so its 32GB choice is left out).
     Product tags say what to leave out on that product:
       hide-option:<name>   a whole option, by its identifier or title
       hide-value:<label>   one choice, e.g. "hide-value:32 GB DDR4"
     Names are matched ignoring case and spaces. A style rule hides them
     before Easify draws them and keeps them hidden through its redraws.
     Products without such tags are never touched.
     Returns false until Easify's settings are on the page. */
  function squash(text) {
    return flatten(text).toLowerCase().replace(/\s+/g, '');
  }

  function cssString(text) {
    return '"' + String(text).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  function hideOptions() {
    var section = document.querySelector('.main-product-template[data-wt-tags]');
    if (!section) return true;
    var optionNames = [];
    var valueNames = [];
    try {
      JSON.parse(section.getAttribute('data-wt-tags')).forEach(function (tag) {
        var match = /^hide-(option|value):\s*(.+)$/i.exec(String(tag));
        if (match) (match[1].toLowerCase() === 'option' ? optionNames : valueNames).push(squash(match[2]));
      });
    } catch (error) { return true; }
    if (!optionNames.length && !valueNames.length) return true;

    var configs = window.TPOConfigs;
    if (!configs) return false;
    var ids = [];
    var labels = [];
    (configs.product_options_easify || configs.product_options_tpo || []).forEach(function (entry) {
      var set = entry && entry.option_set;
      if (!set || !Number(set.status)) return;
      var options = set.options;
      if (typeof options === 'string') {
        try { options = JSON.parse(options); } catch (error) { return; }
      }
      [].forEach.call(options || [], function (option) {
        if (option.id && ids.indexOf(option.id) === -1 &&
            (optionNames.indexOf(squash(option.option_identifier)) !== -1 ||
             optionNames.indexOf(squash(option.title)) !== -1)) {
          ids.push(option.id);
        }
        [].forEach.call(option.option_types || [], function (value) {
          var label = value && value.label;
          if (label && labels.indexOf(label) === -1 && valueNames.indexOf(squash(label)) !== -1) labels.push(label);
        });
      });
    });

    // Easify ids each option box "<render id>,<option id>,<type>", and each
    // choice is a card (or button) holding an input whose value is its label.
    var rules = [];
    if (ids.length) {
      rules.push(ids.map(function (id) { return '.tpo_option-container[id*=",' + id + ',"]'; }).join(',\n'));
    }
    // One rule each, so a browser that can't read :has() still keeps the rest.
    labels.forEach(function (label) {
      var value = '[value=' + cssString(label) + ']';
      rules.push('.tpo_option-container :is(.tpo_swatches-label_position, .tpo_radio-button-wrapper, .container-wrap-selection, label):has(input' + value + ')');
      rules.push('.tpo_option-container option' + value);
    });
    if (!rules.length) return true;

    // The skins lay the cards out with long !important class chains; the
    // two :not(#…) give these rules an id's weight so they always win.
    var lead = 'body:not(#wt-hide-a):not(#wt-hide-b) ';
    var style = el('style', null, rules.map(function (rule) {
      return rule.split(',\n').map(function (part) { return lead + part; }).join(',\n') + ' { display: none !important; }';
    }).join('\n'));
    style.setAttribute('data-wt-hidden-options', '');
    document.head.appendChild(style);
    if (ids.length) silenceOptions(ids);
    return true;
  }

  /* A hidden option can still hold a value (Windows starts on "Windows 11
     Home"), which would go to the cart with the build. Easify builds the
     order's properties from the form's fields and skips disabled ones, as
     does the form itself, so the fields of a hidden option are disabled,
     again whenever Easify redraws them. */
  function silenceOptions(ids) {
    var selector = ids.map(function (id) { return '.tpo_option-container[id*=",' + id + ',"]'; }).join(',');
    var queued = false;
    function apply() {
      queued = false;
      [].forEach.call(document.querySelectorAll(selector), function (group) {
        [].forEach.call(group.querySelectorAll('input, select, textarea'), function (field) {
          if (!field.disabled) field.disabled = true;
        });
      });
    }
    apply();
    // And once more as the build heads for the cart.
    ['pointerdown', 'click', 'submit'].forEach(function (type) {
      document.addEventListener(type, apply, true);
    });
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(apply, 0);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  /* Easify draws its options after the page has loaded, so this waits for
     them, and gives up quietly after half a minute. */
  function boot() {
    var hidden = hideOptions();
    var started = start();
    if (hidden && started) return;
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (!hidden) hidden = hideOptions();
      if (!started) started = start();
      if ((hidden && started) || tries > 60) clearInterval(poll);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
